import { FormEvent, useEffect, useState } from 'react'
import { useI18n } from '../i18n'
import type { MessageKey } from '../i18n'
import { api } from '../api/client'
import { fetchCycle, type CycleEvent } from '../api/cycle'
import { listHoldings, type Holding } from '../api/holdings'
import {
  createJournalEntry, deleteJournalEntry, listJournal,
  type JournalEntry, type JournalKind, type JournalSnapshot,
} from '../api/journal'

type OpenPosition = { id: string; symbol: string; side: string; strike: number; expiry: string }

const ACTIONS: Record<JournalKind, string[]> = {
  option: ['open', 'close', 'roll', 'assign'],
  stock: ['buy', 'sell', 'add', 'reduce', 'hold'],
  no_trade: ['hold'],
}

function snapshotSummary(entry: JournalEntry, t: (k: MessageKey) => string): string {
  const s: JournalSnapshot = entry.snapshot || {}
  if (entry.kind === 'option') {
    return [
      s.strike != null ? `$${s.strike.toFixed(2)}` : null,
      s.premium != null ? `${t('journal.snap.premium')} $${s.premium.toFixed(2)}` : null,
      s.dte != null ? `${s.dte}d` : null,
      s.iv_rank_at_open != null ? `${t('journal.snap.ivRank')} ${s.iv_rank_at_open.toFixed(0)}` : null,
    ].filter(Boolean).join(' · ')
  }
  return [
    s.wti != null ? `WTI ${s.wti.value.toFixed(2)}` : null,
    s.core_cpi_mom != null ? `Core CPI ${s.core_cpi_mom.value.toFixed(2)}%` : null,
    s.dgs10 != null ? `10Y ${s.dgs10.value.toFixed(2)}%` : null,
    s.dgs2 != null ? `2Y ${s.dgs2.value.toFixed(2)}%` : null,
  ].filter(Boolean).join(' · ')
}

function EchoLine({ entry }: { entry: JournalEntry }) {
  const { t } = useI18n()
  const st = entry.settlement
  if (!st) return null
  if (st.unavailable || st.move_pct == null) {
    return <p className="journal-echo muted">{t('journal.echo.unavailable')}</p>
  }
  // §3.3 defines exactly three echo wordings. Anything else — an option roll,
  // a no_trade note with no symbol — has nothing factual to echo, so say nothing.
  if (!entry.symbol) return null
  const key: MessageKey | null = entry.kind === 'no_trade'
    ? 'journal.echo.noTrade'
    : ['sell', 'reduce'].includes(entry.action)
      ? 'journal.echo.sold'
      : ['buy', 'add'].includes(entry.action)
        ? 'journal.echo.bought'
        : null
  if (!key) return null
  const pct = `${st.move_pct >= 0 ? '+' : ''}${(st.move_pct * 100).toFixed(1)}%`
  return (
    <p className="journal-echo">
      {t(key).replace('{sym}', entry.symbol).replace('{pct}', pct)}
      {st.as_of && <span className="muted small"> · {t('events.asOf').replace('{d}', st.as_of.slice(0, 10))}</span>}
    </p>
  )
}

// The "Log a decision" link on a position row and on a holding row carries the
// row it came from, so the composer opens already pointed at it (§4.3).
function prefillFromQuery(): {
  kind: JournalKind; action: string; symbol: string
  position_id: string; holding_id: string; event_id: string; reason: string
} {
  const q = new URLSearchParams(window.location.search)
  const positionId = q.get('position_id') || ''
  const kind: JournalKind = positionId ? 'option' : 'stock'
  return {
    kind,
    action: ACTIONS[kind][0],
    symbol: (q.get('symbol') || '').toUpperCase(),
    position_id: positionId,
    holding_id: q.get('holding_id') || '',
    event_id: '',
    reason: '',
  }
}

export function JournalPage() {
  const { language, t } = useI18n()
  const [items, setItems] = useState<JournalEntry[]>([])
  const [events, setEvents] = useState<CycleEvent[]>([])
  const [positions, setPositions] = useState<OpenPosition[]>([])
  const [holdings, setHoldings] = useState<Holding[]>([])
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [quickReason, setQuickReason] = useState('')
  const [form, setForm] = useState(prefillFromQuery)

  const load = () => {
    listJournal(50).then((r) => setItems(r.items || [])).catch((err: Error) => setError(err.message))
  }

  useEffect(() => {
    load()
    api<{ positions: OpenPosition[] }>('/v1/positions').then((r) => setPositions(r.positions || [])).catch(() => setPositions([]))
    listHoldings().then((r) => setHoldings(r.items || [])).catch(() => setHoldings([]))
  }, [])

  // 180 days: FOMC data runs to 2027 and a position opened two months ahead of
  // one has to be bindable to it (contract C19).
  useEffect(() => { fetchCycle(180).then((c) => setEvents(c.events || [])).catch(() => setEvents([])) }, [language])

  const setKind = (kind: JournalKind) => {
    setForm({ ...form, kind, action: ACTIONS[kind][0], position_id: '', holding_id: '', symbol: '' })
  }

  const post = async (body: Parameters<typeof createJournalEntry>[0]) => {
    setSaving(true)
    setError('')
    try {
      await createJournalEntry(body)
      load()
      return true
    } catch (err) {
      setError((err as Error).message)
      return false
    } finally {
      setSaving(false)
    }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const holding = holdings.find((h) => h.id === form.holding_id)
    const position = positions.find((p) => p.id === form.position_id)
    const ok = await post({
      kind: form.kind,
      action: form.action,
      reason: form.reason.trim(),
      ...(form.kind === 'option' ? { position_id: form.position_id, symbol: position?.symbol } : {}),
      ...(form.kind === 'stock' ? {
        ...(form.holding_id ? { holding_id: form.holding_id } : {}),
        symbol: (holding?.symbol || form.symbol).toUpperCase(),
      } : {}),
      ...(form.event_id ? { event_id: form.event_id } : {}),
    })
    if (ok) setForm({ ...form, reason: '', symbol: '', position_id: '', holding_id: '', event_id: '' })
  }

  const quick = async () => {
    if (!quickReason.trim()) return
    if (await post({ kind: 'no_trade', action: 'hold', reason: quickReason.trim() })) setQuickReason('')
  }

  // Past events fall out of the 45-day window, so an id that is no longer in
  // `events` still has to read as an event rather than as a slug.
  const eventLabel = (id?: string | null) => {
    if (!id) return ''
    const known = events.find((e) => e.id === id)
    if (known) return known.label
    const earnings = /^earnings:([A-Z.]+):(\d{4}-\d{2}-\d{2})$/.exec(id)
    if (earnings) return `${earnings[1]} ${t('journal.eventEarnings')} · ${earnings[2]}`
    const macro = /^([a-z]+)-(\d{4}-\d{2}-\d{2})$/.exec(id)
    if (macro) return `${macro[1].toUpperCase()} · ${macro[2]}`
    return id
  }

  return (
    <section className="scanner">
      <header className="scanner__head">
        <div>
          <p className="kicker">{t('journal.kicker')}</p>
          <h1>{t('journal.title')}</h1>
          <p className="muted">{t('journal.subtitle')}</p>
        </div>
      </header>

      {error && <p className="form-message form-message--error">{error}</p>}

      <div className="journal-quick">
        <input
          value={quickReason}
          onChange={(e) => setQuickReason(e.target.value)}
          placeholder={t('journal.quick.placeholder')}
        />
        <button type="button" className="button button--secondary" onClick={quick} disabled={saving || !quickReason.trim()}>
          {t('journal.quick.button')}
        </button>
      </div>

      <form className="position-form" onSubmit={submit}>
        <label>{t('journal.kind')}
          <select value={form.kind} onChange={(e) => setKind(e.target.value as JournalKind)}>
            <option value="option">{t('journal.kind.option')}</option>
            <option value="stock">{t('journal.kind.stock')}</option>
            <option value="no_trade">{t('journal.kind.noTrade')}</option>
          </select>
        </label>
        {form.kind === 'option' && (
          <label>{t('journal.position')}
            <select required value={form.position_id} onChange={(e) => setForm({ ...form, position_id: e.target.value })}>
              <option value="">{t('journal.choosePosition')}</option>
              {positions.map((p) => (
                <option key={p.id} value={p.id}>{p.symbol} {p.side} ${p.strike} · {p.expiry.slice(0, 10)}</option>
              ))}
            </select>
          </label>
        )}
        {form.kind === 'stock' && (
          <>
            <label>{t('journal.holding')}
              <select value={form.holding_id} onChange={(e) => setForm({ ...form, holding_id: e.target.value })}>
                <option value="">{t('journal.chooseHolding')}</option>
                {holdings.map((h) => <option key={h.id} value={h.id}>{h.symbol} · {h.qty}</option>)}
              </select>
            </label>
            {!form.holding_id && (
              <label>{t('journal.symbol')}
                <input required value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} placeholder="AAPL" />
              </label>
            )}
          </>
        )}
        <label>{t('journal.action')}
          <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}>
            {ACTIONS[form.kind].map((a) => <option key={a} value={a}>{t(`journal.action.${a}` as MessageKey)}</option>)}
          </select>
        </label>
        <label>{t('journal.bindEvent')}
          <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })}>
            <option value="">{t('journal.noEvent')}</option>
            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.label} · {ev.occurs_at.slice(0, 10)}</option>)}
          </select>
        </label>
        <label className="full-row">{t('journal.reason')}
          <input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value.slice(0, 500) })} placeholder={t('journal.reason.placeholder')} maxLength={500} />
        </label>
        <button type="submit" className="button button--primary" disabled={saving}>{saving ? t('journal.saving') : t('journal.save')}</button>
      </form>

      <ul className="journal-list">
        {items.length === 0 && <li className="muted">{t('journal.empty')}</li>}
        {items.map((entry) => (
          <li key={entry.id} className="journal-entry">
            <div className="journal-entry__head">
              <span className={`pill pill--${entry.kind === 'option' ? 'blue' : entry.kind === 'stock' ? 'green' : 'amber'}`}>
                {t(`journal.kind.${entry.kind === 'no_trade' ? 'noTrade' : entry.kind}` as MessageKey)}
              </span>
              {entry.symbol && <strong>{entry.symbol}</strong>}
              <span className="badge">{t(`journal.action.${entry.action}` as MessageKey)}</span>
              {entry.event_id && <span className="badge badge--muted">{eventLabel(entry.event_id)}</span>}
              <span className="muted small">{entry.created_at.slice(0, 10)}</span>
              <button type="button" className="button button--ghost button--small" onClick={() => deleteJournalEntry(entry.id).then(load).catch((err: Error) => setError(err.message))}>
                {t('journal.delete')}
              </button>
            </div>
            <p className="journal-entry__reason">{entry.reason}</p>
            {snapshotSummary(entry, t) && (
              <p className="muted small">{snapshotSummary(entry, t)}</p>
            )}
            <EchoLine entry={entry} />
          </li>
        ))}
      </ul>
    </section>
  )
}
